/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { Party } from './Party';
import {
    PartyFromJSON,
    PartyFromJSONTyped,
    PartyToJSON,
    PartyToJSONTyped,
} from './Party';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Location } from './Location';
import {
    LocationFromJSON,
    LocationFromJSONTyped,
    LocationToJSON,
    LocationToJSONTyped,
} from './Location';

/**
 * An event is a fact that has occurred at a particular location and time.
 * @export
 * @interface Event
 */
export interface Event {
    /**
     * Date and time when the event occurred in UTC
     * @type {Date}
     * @memberof Event
     */
    actualDateTime: Date;
    /**
     * Additional description of the event. Free text.
     * @type {string}
     * @memberof Event
     */
    description?: string;
    /**
     * List of the documents used for the activity.
     * @type {Array<Document>}
     * @memberof Event
     */
    documents?: Array<Document>;
    /**
     * The type of an event within a domain.
     * @type {string}
     * @memberof Event
     */
    eventType: string;
    /**
     * The domain of the event (e.g. BUSINESS, ISSUE).
     * @type {string}
     * @memberof Event
     */
    domain: EventDomainEnum;
    /**
     * Code of the event.
     * @type {string}
     * @memberof Event
     */
    code: string;
    /**
     * Location where the event occurred.
     * @type {Location}
     * @memberof Event
     */
    location?: Location;
    /**
     * The party who triggered the event.
     * @type {Party}
     * @memberof Event
     */
    createdBy?: Party;
    /**
     * Reason code to identify the reason for the issue declared in the event. This is only used for events in the ISSUE domain.
     * @type {string}
     * @memberof Event
     */
    reasonCode?: string;
    /**
     * The party who is accountable for the issue. This is only used for events in the ISSUE domain.
     * @type {Party}
     * @memberof Event
     */
    accountableParty?: Party;
    /**
     * Internal unique identifier of the resource. Id can have different formats like UUID v4, UUID v7, or any other string calculated.
     * @type {string}
     * @memberof Event
     */
    id: string;
    /**
     * Identifier of the member that created the event.
     * @type {string}
     * @memberof Event
     */
    memberIdentifier?: string;
    /**
     * Creation Date and time of the event in the source system.
     * @type {Date}
     * @memberof Event
     */
    creationDateTime?: Date;
    /**
     * Traceparent of the process that created the event.
     * @type {string}
     * @memberof Event
     */
    creationTraceparent?: string;
}


/**
 * @export
 */
export const EventDomainEnum = {
    Business: 'BUSINESS',
    Issue: 'ISSUE'
} as const;
export type EventDomainEnum = typeof EventDomainEnum[keyof typeof EventDomainEnum];


/**
 * Check if a given object implements the Event interface.
 */
export function instanceOfEvent(value: object): value is Event {
    if (!('actualDateTime' in value) || value['actualDateTime'] === undefined) return false;
    if (!('eventType' in value) || value['eventType'] === undefined) return false;
    if (!('domain' in value) || value['domain'] === undefined) return false;
    if (!('code' in value) || value['code'] === undefined) return false;
    if (!('id' in value) || value['id'] === undefined) return false;
    return true;
}

export function EventFromJSON(json: any): Event {
    return EventFromJSONTyped(json, false);
}

export function EventFromJSONTyped(json: any, ignoreDiscriminator: boolean): Event {
    if (json == null) {
        return json;
    }
    return {
        
        'actualDateTime': (new Date(json['actualDateTime'])),
        'description': json['description'] == null ? undefined : json['description'],
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'eventType': json['eventType'],
        'domain': json['domain'],
        'code': json['code'],
        'location': json['location'] == null ? undefined : LocationFromJSON(json['location']),
        'createdBy': json['createdBy'] == null ? undefined : PartyFromJSON(json['createdBy']),
        'reasonCode': json['reasonCode'] == null ? undefined : json['reasonCode'],
        'accountableParty': json['accountableParty'] == null ? undefined : PartyFromJSON(json['accountableParty']),
        'id': json['id'],
        'memberIdentifier': json['memberIdentifier'] == null ? undefined : json['memberIdentifier'],
        'creationDateTime': json['creationDateTime'] == null ? undefined : (new Date(json['creationDateTime'])),
        'creationTraceparent': json['creationTraceparent'] == null ? undefined : json['creationTraceparent'],
    };
}

export function EventToJSON(json: any): Event {
    return EventToJSONTyped(json, false);
}

export function EventToJSONTyped(value?: Event | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'actualDateTime': value['actualDateTime'].toISOString(),
        'description': value['description'],
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'eventType': value['eventType'],
        'domain': value['domain'],
        'code': value['code'],
        'location': LocationToJSON(value['location']),
        'createdBy': PartyToJSON(value['createdBy']),
        'reasonCode': value['reasonCode'],
        'accountableParty': PartyToJSON(value['accountableParty']),
        'id': value['id'],
        'memberIdentifier': value['memberIdentifier'],
        'creationDateTime': value['creationDateTime'] == null ? value['creationDateTime'] : value['creationDateTime'].toISOString(),
        'creationTraceparent': value['creationTraceparent'],
    };
}

